import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const NOTES = [
  {
    testId: "lifestyle-heel",
    index: "i",
    title: "The Power of the Heel",
    body: "An evening is carried in the walk. For the heeled sandal that holds a room from arrival to the last reel, our editors send guests directly to the Selfridges shoe salon.",
    partner: "Selfridges, London - Shoe Galleries",
    href: "https://www.selfridges.com/GB/en/cat/shoes/womens/sandals/heeled-sandals/high-heel/?pn=2",
  },
  {
    testId: "lifestyle-lingerie",
    index: "ii",
    title: "La Dame de France",
    body: "Luxury lingerie in the French manner - the quiet foundation beneath the gown. La Dame de France dress our guests from the inside out, with a discretion we rather admire.",
    partner: "La Dame de France - Luxury Lingerie",
    href: "https://ladamedefrance.com",
  },
  {
    testId: "lifestyle-hosiery",
    index: "iii",
    title: "Sublimate Legs - The Power of Hosiery",
    body: "Seamed nylons, a fully-fashioned welt, the faint shimmer of a well-chosen stocking. Cervin have made hosiery in France since 1919, and remain the only house we recommend by name.",
    partner: "Cervin, Paris - Fine Hosiery since 1919",
    href: "https://www.cervin-store.com",
  },
];

export const Lifestyle = () => {
  return (
    <section
      id="lifestyle"
      data-testid="lifestyle-section"
      className="relative bg-[color:var(--bt-bg)] px-6 md:px-10 py-32 md:py-56"
    >
      <div className="mx-auto max-w-[1500px]">
        {/* Kicker */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="grid md:grid-cols-12 gap-6 items-end mb-14 md:mb-20"
        >
          <div className="md:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
              <span
                data-testid="lifestyle-eyebrow"
                className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
              >
                Chapter III ½ · The Concierge
              </span>
            </div>
            <h2
              data-testid="lifestyle-heading"
              className="font-serif-display text-[9vw] md:text-[5.5vw] leading-[0.95] tracking-tight text-[color:var(--bt-ink)]"
            >
              The art of the{" "}
              <span className="italic font-light text-[color:var(--bt-gold)]">
                evening.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 md:pb-4">
            <p className="text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed max-w-md">
              A short list of houses our editors quietly return to - the
              smallest details of the evening, considered and delivered without
              ceremony.
            </p>
          </div>
        </motion.div>

        {/* Three lifestyle notes */}
        <div className="flex flex-col">
          {NOTES.map((note) => (
            <LifestyleNote key={note.testId} {...note} />
          ))}
        </div>

        {/* The Evening Standard - male apparel */}
        <div
          id="evening-standard"
          data-testid="evening-standard"
          className="mt-24 md:mt-36"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="flex items-center gap-4 mb-4"
          >
            <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
            <span className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
              For the Gentleman
            </span>
          </motion.div>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.05, ease: EASE }}
            data-testid="evening-standard-heading"
            className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[color:var(--bt-ink)] mb-12 md:mb-16"
          >
            The Evening{" "}
            <span className="italic font-light text-[color:var(--bt-gold)]">
              Standard.
            </span>
          </motion.h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <ApparelBlock
              testId="apparel-boss"
              category="The Modern Dinner Jacket"
              brand="BOSS"
              body="The contemporary cut, cleanly done. For the guest who prefers a sharp modern line to a heritage silhouette, the BOSS dinner jacket holds its shape from reception to the last dance."
              href="https://www.hugoboss.com/uk/men-dinner-jackets/"
              image="https://images.unsplash.com/photo-1522968439036-e6338d0ed84f?auto=format&fit=crop&w=1200&q=80"
            />
            <ApparelBlock
              testId="apparel-tomford"
              category="The Definitive Tuxedo"
              brand="TOM FORD"
              body="There is black tie, and there is Tom Ford. The shawl lapel, the grosgrain, the shoulder - the definitive tuxedo against which all others are quietly measured."
              href="https://www.tomfordfashion.com/en-us/men/ready-to-wear/suits-tuxedos/"
              image="https://images.unsplash.com/photo-1653936663217-f0a1ac722c96?auto=format&fit=crop&w=1200&q=80"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const LifestyleNote = ({ index, title, body, partner, href, testId }) => (
  <motion.a
    href={href}
    data-testid={testId}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE }}
    className="group grid md:grid-cols-12 gap-4 md:gap-8 py-10 md:py-12 border-t border-[color:var(--bt-gold-line)] last:border-b hover:bg-[color:var(--bt-gold-glow)] transition-colors"
  >
    <div className="md:col-span-1 font-serif-display italic text-[color:var(--bt-gold)] text-2xl md:text-4xl leading-none">
      {index}.
    </div>
    <div className="md:col-span-6">
      <h3 className="font-serif-display text-3xl md:text-5xl leading-tight tracking-tight text-[color:var(--bt-ink)] group-hover:text-[color:var(--bt-gold)] transition-colors">
        {title}
      </h3>
      <p className="mt-4 text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed max-w-xl">
        {body}
      </p>
    </div>
    <div className="md:col-span-5 flex md:flex-col md:items-end md:justify-between gap-4">
      <span className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] md:text-right">
        {partner}
      </span>
      <span className="flex items-center gap-3 text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)] whitespace-nowrap">
        <span className="w-8 h-px bg-[color:var(--bt-gold)] transition-all duration-500 group-hover:w-14" />
        Discover
        <ArrowUpRight size={14} />
      </span>
    </div>
  </motion.a>
);

const ApparelBlock = ({ category, brand, body, href, image, testId }) => (
  <motion.a
    href={href}
    data-testid={testId}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE }}
    className="group relative block border border-[color:var(--bt-gold-line)] hover:border-[color:var(--bt-gold)] transition-colors duration-500"
  >
    <div className="relative overflow-hidden h-[46vh] md:h-[58vh]">
      <img
        src={image}
        alt={`${brand} - ${category}`}
        loading="lazy"
        className="h-full w-full object-cover grayscale contrast-[1.05] transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bt-bg)] via-[color:var(--bt-bg)]/40 to-transparent" />
      {/* Corner ticks */}
      <span className="absolute top-3 left-3 w-3 h-px bg-[color:var(--bt-gold)]" />
      <span className="absolute top-3 left-3 h-3 w-px bg-[color:var(--bt-gold)]" />
      <span className="absolute top-3 right-3 w-3 h-px bg-[color:var(--bt-gold)]" />
      <span className="absolute top-3 right-3 h-3 w-px bg-[color:var(--bt-gold)]" />
    </div>
    <div className="p-7 md:p-9">
      <div className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)] mb-3">
        {category}
      </div>
      <h4 className="font-serif-display text-4xl md:text-5xl leading-none tracking-tight text-[color:var(--bt-ink)] group-hover:text-[color:var(--bt-gold)] transition-colors">
        {brand}
      </h4>
      <p className="mt-5 text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed max-w-md">
        {body}
      </p>
      <span className="mt-7 flex items-center gap-3 text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
        <span className="w-8 h-px bg-[color:var(--bt-gold)] transition-all duration-500 group-hover:w-14" />
        Discover
        <ArrowUpRight size={14} />
      </span>
    </div>
  </motion.a>
);

export default Lifestyle;
